import {
	useRef,
	useEffect,
	ReactNode,
	Children,
	cloneElement,
	isValidElement,
	useCallback,
	useMemo,
	ReactElement,
} from 'react';

interface ConversationWrapperProps {
	children: ReactNode;
	items: number;
}

const ConversationWrapper = ({ children, items }: ConversationWrapperProps) => {
	const outerDivRef = useRef<HTMLDivElement | null>(null);
	const lastItemRef = useRef<HTMLDivElement | null>(null);

	// Scroll to the latest conversation item
	const scrollToLastItem = useCallback(() => {
		if (items > 0 && lastItemRef.current && outerDivRef.current) {
			const lastItem = lastItemRef.current;
			const outerDiv = outerDivRef.current;

			// Use requestAnimationFrame for better performance
			requestAnimationFrame(() => {
				outerDiv.scrollTo({
					top: lastItem.offsetTop,
					left: 0,
					behavior: 'smooth',
				});
			});
		}
	}, [items]);

	useEffect(() => {
		scrollToLastItem();
	}, [scrollToLastItem]);

	// Memoize children processing to avoid unnecessary re-processing
	// TODO: Make sure to test this when we get actual responses from endpoint
	const enhancedChildren = useMemo(() => {
		const childrenArray = Children.toArray(children);
		return childrenArray.map((child, idx) => {
			if (idx === childrenArray.length - 1 && isValidElement(child)) {
				return cloneElement(child as ReactElement, { ref: lastItemRef });
			}
			return child;
		});
	}, [children]);

	return (
		<div className="relative h-full w-full">
			<div
				className="relative h-full overflow-y-scroll"
				ref={outerDivRef}
				style={{ scrollbarWidth: 'none' }}
				tabIndex={0}
			>
				<div className="relative transition-all duration-300">
					{enhancedChildren}
				</div>
			</div>
		</div>
	);
};

export default ConversationWrapper;
